import React from 'react';
import { 
  FaShopify, 
  FaReact, 
  FaNodeJs, 
  FaStripe, 
  FaExternalLinkAlt, 
  FaShieldAlt, 
  FaLayerGroup,
  FaAws,
  FaDocker,
  FaGithub
} from 'react-icons/fa';
import { 
  SiFlutter, 
  SiTwilio, 
  SiOpenai, 
  SiNextdotjs, 
  SiPostgresql, 
  SiTailwindcss, 
  SiRedis, 
  SiVercel 
} from 'react-icons/si';

/**
 * Verified Engineering Standards & Authoritative Tech Stack Citations
 * Links directly to official documentation, standards, and framework specifications
 * to establish authentic domain authority, high-quality dofollow link signals, and technical credibility.
 */
const techCitations = [
  {
    name: 'Shopify 2.0 & Hydrogen',
    category: 'E-Commerce Architecture',
    icon: <FaShopify className="text-2xl text-[#96bf48]" aria-hidden="true" focusable="false" />,
    description: 'Custom Liquid themes, Section Rendering APIs, and Headless Hydrogen Storefronts.',
    url: 'https://shopify.dev/docs',
    badge: 'Shopify Dev Standard'
  },
  {
    name: 'Flutter Cross-Platform',
    category: 'Mobile Engineering',
    icon: <SiFlutter className="text-2xl text-[#02569B]" aria-hidden="true" focusable="false" />,
    description: 'Hardware-accelerated 60fps native compiling for iOS and Android from a single codebase.',
    url: 'https://flutter.dev',
    badge: 'Google Flutter SDK'
  },
  {
    name: 'React 18 & TypeScript',
    category: 'Frontend Performance',
    icon: <FaReact className="text-2xl text-[#61DAFB]" aria-hidden="true" focusable="false" />,
    description: 'Strict type safety, component-driven modular UI, and Core Web Vitals optimization.',
    url: 'https://react.dev',
    badge: 'React Documentation'
  },
  {
    name: 'Stripe Billing & Payments',
    category: 'Financial Infrastructure',
    icon: <FaStripe className="text-3xl text-[#635BFF]" aria-hidden="true" focusable="false" />,
    description: 'PCI-DSS Level 1 compliant tiered billing, usage-based subscriptions, and automated webhooks.',
    url: 'https://stripe.com/docs/billing',
    badge: 'Stripe Global Standard'
  },
  {
    name: 'Node.js & Express REST',
    category: 'Backend Microservices',
    icon: <FaNodeJs className="text-2xl text-[#339933]" aria-hidden="true" focusable="false" />,
    description: 'Non-blocking I/O event loops, JWT token authentication, and optimized MySQL/PostgreSQL pooling.',
    url: 'https://nodejs.org/docs',
    badge: 'Node.js Foundation'
  },
  {
    name: 'OpenAI & Claude LLMs',
    category: 'Intelligent AI Workflows',
    icon: <SiOpenai className="text-2xl text-gray-900" aria-hidden="true" focusable="false" />,
    description: 'Embeddings, retrieval-augmented generation (RAG), and autonomous structured task agents.',
    url: 'https://platform.openai.com/docs',
    badge: 'OpenAI Developer Docs'
  },
  {
    name: 'Twilio Voice Telephony',
    category: '24/7 AI Voice Telephony',
    icon: <SiTwilio className="text-2xl text-[#F22F46]" aria-hidden="true" focusable="false" />,
    description: 'Low-latency programmable voice SIP trunking connected to real-time LLM speech pipelines.',
    url: 'https://www.twilio.com/docs/voice',
    badge: 'Twilio Voice API'
  },
  {
    name: 'Schema.org & W3C Standards',
    category: 'Semantic SEO Compliance',
    icon: <FaLayerGroup className="text-2xl text-red-600" aria-hidden="true" focusable="false" />,
    description: 'Structured JSON-LD entity graph for search engine comprehension and rich SERP results.',
    url: 'https://schema.org',
    badge: 'W3C Semantic Standards'
  },
  {
    name: 'Next.js & React Server Components',
    category: 'Edge Rendering & SSR',
    icon: <SiNextdotjs className="text-2xl text-black" aria-hidden="true" focusable="false" />,
    description: 'Sub-second Server-Side Rendering (SSR), Incremental Static Regeneration (ISR), and Edge middleware.',
    url: 'https://nextjs.org/docs',
    badge: 'Vercel Next.js Spec'
  },
  {
    name: 'PostgreSQL Relational DB',
    category: 'Enterprise Database',
    icon: <SiPostgresql className="text-2xl text-[#336791]" aria-hidden="true" focusable="false" />,
    description: 'ACID-compliant relational schemas, JSONB indexing, connection pooling, and multi-tenant partitioning.',
    url: 'https://www.postgresql.org/docs/',
    badge: 'PostgreSQL Global Dev'
  },
  {
    name: 'AWS Cloud Infrastructure',
    category: 'Enterprise Cloud & S3',
    icon: <FaAws className="text-2xl text-[#FF9900]" aria-hidden="true" focusable="false" />,
    description: 'High-availability VPC architectures, AWS Lambda serverless execution, and encrypted S3 bucket storage.',
    url: 'https://docs.aws.amazon.com/',
    badge: 'AWS Well-Architected'
  },
  {
    name: 'Tailwind CSS Architecture',
    category: 'Modern Design System',
    icon: <SiTailwindcss className="text-2xl text-[#06B6D4]" aria-hidden="true" focusable="false" />,
    description: 'Zero runtime CSS overhead, responsive breakpoint scaling, and dark-mode design token architectures.',
    url: 'https://tailwindcss.com/docs',
    badge: 'Tailwind Labs Standards'
  },
  {
    name: 'Redis In-Memory Caching',
    category: 'Microsecond Data Layer',
    icon: <SiRedis className="text-2xl text-[#DC382D]" aria-hidden="true" focusable="false" />,
    description: 'Sub-millisecond session state management, distributed rate limiting, and Pub/Sub message queuing.',
    url: 'https://redis.io/docs/',
    badge: 'Redis Enterprise Docs'
  },
  {
    name: 'Docker Containerization',
    category: 'Microservices & DevOps',
    icon: <FaDocker className="text-2xl text-[#2496ED]" aria-hidden="true" focusable="false" />,
    description: 'Deterministic containerized builds, multi-stage images, and zero-downtime deployment pipelines.',
    url: 'https://docs.docker.com/',
    badge: 'Docker Open Standard'
  },
  {
    name: 'GitHub Open Source & CI/CD',
    category: 'Version Control & Pipelines',
    icon: <FaGithub className="text-2xl text-gray-900" aria-hidden="true" focusable="false" />,
    description: 'Git source control, GitHub Actions automated test suites, branch protection, and vulnerability auditing.',
    url: 'https://docs.github.com/',
    badge: 'GitHub Developer Docs'
  },
  {
    name: 'Vercel Edge Network',
    category: 'Global Edge Acceleration',
    icon: <SiVercel className="text-2xl text-black" aria-hidden="true" focusable="false" />,
    description: 'Worldwide Anycast CDN distribution, instant preview environments, and automated edge routing.',
    url: 'https://vercel.com/docs',
    badge: 'Vercel Platform Docs'
  }
];

const TechStackCitations = () => {
  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 via-white to-gray-50 border-t border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-bold uppercase tracking-wider mb-3">
            <FaShieldAlt className="text-red-600" aria-hidden="true" />
            <span>Verified Architectural Standards</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 tracking-tight">
            Engineered on <span className="text-red-600">Industry-Standard</span> Frameworks
          </h2>
          <p className="mt-3 text-gray-600 text-sm sm:text-base leading-relaxed">
            Every Shopify store, mobile app, SaaS platform, and AI workflow we deliver adheres strictly to official developer documentation, open standards, and enterprise compliance.
          </p>
        </div>

        {/* Grid of Citations / Standards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {techCitations.map((item, idx) => (
            <div 
              key={idx}
              className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-sm hover:shadow-md hover:border-red-300 transition-all duration-300 flex flex-col justify-between group"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center group-hover:scale-105 transition-transform"
                    aria-hidden="true"
                  >
                    {React.isValidElement(item.icon)
                      ? React.cloneElement(item.icon, {
                          'aria-hidden': 'true',
                          focusable: 'false'
                        })
                      : item.icon}
                  </div>
                  <span className="text-[11px] font-bold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-md">
                    {item.badge}
                  </span>
                </div>

                <div className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1">
                  {item.category}
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                  {item.name}
                </h3>
                <p className="text-gray-600 text-xs leading-relaxed mb-4">
                  {item.description}
                </p>
              </div>

              {/* Official Documentation Backlink */}
              <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold text-gray-700 hover:text-red-600 inline-flex items-center gap-1.5 transition-colors"
                  aria-label={`Read official ${item.name} documentation`}
                >
                  <span>Official Reference Docs</span>
                  <FaExternalLinkAlt className="text-[10px]" aria-hidden="true" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Trust Bottom Notice */}
        <div className="mt-10 p-5 rounded-2xl bg-white border border-gray-200 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <FaShieldAlt className="text-lg" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Zero Proprietary Lock-In Guarantee</p>
              <p className="text-xs text-gray-500">You retain 100% full intellectual property ownership, Git repository access, and production credentials upon project delivery.</p>
            </div>
          </div>
          <a
            href="/Form"
            className="shrink-0 bg-red-600 hover:bg-red-700 text-white font-bold text-xs px-5 py-2.5 rounded-xl shadow transition"
          >
            Start Project Discovery
          </a>
        </div>

      </div>
    </section>
  );
};

export default TechStackCitations;
